import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button as AntButton, DatePicker, Descriptions, Modal } from 'antd';
import moment from 'moment';
import useNotification from '../../../../hooks/useNotification';
import useAxios from '../../../../hooks/useAxios';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/rootReducer';
import {
  createEntranceSchedule,
  updateEntranceSchedule,
} from '../../../../api/placeTest.api';
import Loader from '../../../Loader';
import ErrorResponse from '../../../ErrorResponse';
import Checkbox from '../../../Checkbox';
import CustomTimePicker from '../../../CustomTimePicker';
import { IEntranceSchedule } from '../../../../models/placeTest.model';
import Radio from '../../../Radio';
import SelectEduStep from '../../../SelectEduStep';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  getSchedules: () => void;
  record?: IEntranceSchedule;
}

const CreateExamScheduleModal: FC<IProps> = ({
  visible,
  setVisible,
  getSchedules,
  record,
}) => {
  const { t } = useTranslation();
  const { infoNotification, successNotification } = useNotification();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);
  const { data: res, isLoading, error, execute } = useAxios();

  const [eduStepCode, setEduStepCode] = useState('');
  const [examDate, setExamDate] = useState('');
  const [srtTime, setSrtTime] = useState('');
  const [quota, setQuota] = useState('');
  const [campusExamYn, setCampusExamYn] = useState<'Y' | 'N'>('Y');
  const [onlineExamYn, setOnlineExamYn] = useState<'Y' | 'N'>('N');
  const [openYn, setOpenYn] = useState<'Y' | 'N'>('Y');
  const [closeYn, setCloseYn] = useState<'Y' | 'N'>('N');
  const [isCreate, setIsCreate] = useState(true);

  useEffect(() => {
    if (record) {
      setEduStepCode(record.eduStepCode);
      setExamDate(record.examDate);
      setSrtTime(record.srtTime);
      setQuota(String(record.quota));
      setCampusExamYn(record.campusExamYn);
      setOnlineExamYn(record.onlineExamYn);
      setOpenYn(record.openYn);
      setCloseYn(record.closeYn);
      setIsCreate(false);
    }
  }, [record]);

  useEffect(() => {
    if (res && !isLoading) {
      getSchedules();
      successNotification(t('common.message.save1'));
      setVisible(false);
    }
  }, [res, isLoading]);

  const onSubmit = () => {
    if (!currentCampus) {
      return infoNotification(t('entrance.schedule.message1'));
    }
    if (!examDate) {
      return infoNotification(t('entrance.schedule.message2'));
    }
    if (moment(examDate).isBefore(moment(), 'day')) {
      return infoNotification(t('entrance.schedule.message3'));
    }
    if (!srtTime) {
      return infoNotification(t('entrance.schedule.message4'));
    }
    if (!quota) {
      return infoNotification(t('entrance.schedule.message5'));
    }
    if (campusExamYn === 'N' && onlineExamYn === 'N') {
      return infoNotification(t('entrance.schedule.message6'));
    }

    const req: any = {
      eduStepCode,
      examDate,
      srtTime,
      quota: Number(quota),
      campusExamYn,
      onlineExamYn,
      openYn,
      closeYn,
    };
    if (!isCreate) {
      req.platScheduleCode = record?.platScheduleCode;
    }

    isCreate
      ? execute(createEntranceSchedule(currentCampus, req))
      : execute(updateEntranceSchedule(currentCampus, req));
  };

  const checkIsDisabled = (checkDate = true) => {
    const currentDate = moment(serverDate).format('YYYYMMDD');
    const nowTime = moment().format('HH:mm');

    if (isCreate || !record) {
      return false;
    }
    if (record.applyCnt > 0) {
      return true;
    }
    if (
      checkDate &&
      (Number(examDate) < Number(currentDate) || (Number(examDate) === Number(currentDate) && parseInt(srtTime.replace(':', ''), 10) < parseInt(nowTime.replace(':', ''), 10)))
    ) {
      return true;
    }
    
    return false;
  };

  return (
    <>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <Modal
        title={isCreate ? t('entrance.schedule.title2') : t('entrance.schedule.4')}
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <div className='comm-btn__area justify-content-center'>			  
            <AntButton key="submit" className='btn-basic pink' onClick={onSubmit}>
              {t('common.button.save')}
            </AntButton>
            <AntButton key="cancel" className='btn-basic gray' onClick={() => setVisible(false)}>
              {t('common.button.close')}
            </AntButton>
          </div>
        ]}
      >
        <div className='comm-table__area rowType edit mt-0'>
          {isLoading ? <Loader /> : null}
          <ErrorResponse error={error} />

          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('common.label.1')}>
              <div className="flex">
                <SelectEduStep
                  eduStepCode={eduStepCode}
                  setEduStepCode={setEduStepCode}
                  width={100}
                  disabled={!isCreate}
                />
                <div className="v-line ml-1">
                  {t('entrance.schedule.5')}
                </div>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('entrance.schedule.column1')}>
              <div className="date-picker__area w-120p">
                <DatePicker
                  value={examDate ? moment(examDate) : undefined}
                  onChange={(e) => {
                    if (e === null) setExamDate('');
                    else setExamDate(moment(e).format('YYYYMMDD'));
                  }}
                  disabled={checkIsDisabled(false)}
                />
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('entrance.schedule.column2')}>
              <div className="time-picker__area w-120p">
                <CustomTimePicker
                  value={srtTime}
                  onSelect={(value) => setSrtTime(moment(value).format('HH:mm'))}
                  minuteStep={30}
                  disabled={checkIsDisabled(false)}
                />
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('entrance.schedule.column3')}>
              <div className="flex">
                <input
                  className="form-control input-text w-100p"
                  type="number"
                  value={quota}
                  onChange={(e) => setQuota(e.target.value)}
                />
                <span className="v-line ml-1">{`(${t(
                  'entrance.schedule.text2',
                )})`}</span>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('entrance.schedule.column13')}>
              <div className="w-240p">
                <Checkbox
                  className="ml-2"
                  checked={campusExamYn === 'Y'}
                  onChange={() =>
                    setCampusExamYn(campusExamYn === 'Y' ? 'N' : 'Y')
                  }
                  text={t('entrance.schedule.option3')}
                />
                <Checkbox
                  className="ml-2"
                  checked={onlineExamYn === 'Y'}
                  onChange={() =>
                    setOnlineExamYn(onlineExamYn === 'Y' ? 'N' : 'Y')
                  }
                  text={t('entrance.schedule.option4')}
                />
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('entrance.schedule.column7')}>
              <div className="pl-2">
                <Radio
                  checked={openYn === 'Y'}
                  onChange={() => setOpenYn('Y')}
                  text={t('common.option.yes')}
                />
                <Radio
                  className="ml-4"
                  checked={openYn === 'N'}
                  onChange={() => setOpenYn('N')}
                  text={t('common.option.no')}
                />
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('entrance.schedule.6')}>
              <div className="pl-2">
                <Radio
                  checked={closeYn === 'Y'}
                  onChange={() => setCloseYn('Y')}
                  text={t('common.option.yes')}
                  disabled={checkIsDisabled()}
                />
                <Radio
                  className="ml-4"
                  checked={closeYn === 'N'}
                  onChange={() => setCloseYn('N')}
                  text={t('common.option.no')}
                  disabled={checkIsDisabled()}
                />
              </div>
            </Descriptions.Item>
          </Descriptions>
        </div>
      </Modal>
    </>
  );
};

export default CreateExamScheduleModal;
